import React from "react";
import { Box, Button, Flex, SimpleGrid, Spinner, Stack, Text } from "@chakra-ui/react";
import { FiCheck, FiCompass, FiShield } from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";
import { usePerfilRegistroContext, useRegisterCamp, useRegisterClub } from "features/perfil/registro/hooks";
import { formatFullName } from "../../../utils/names";
import FamiliaEditorLayout from "../Familia/FamiliaEditorLayout";
import RedirectStatusPage from "../../main/Status/RedirectStatusPage";

const SIZES = {
  nino_s: "Ni\u00f1o S", nino_m: "Ni\u00f1o M", nino_l: "Ni\u00f1o L", nino_xl: "Ni\u00f1o XL",
  adulto_s: "Adulto S", adulto_m: "Adulto M", adulto_l: "Adulto L", adulto_xl: "Adulto XL", adulto_xxl: "Adulto XXL",
};

export default function EditarRegistroPerfil() {
  const { socioId, programa } = useParams();
  const navigate = useNavigate();
  const { data = {}, isLoading, refetch } = usePerfilRegistroContext();
  const socios = (data.data || data).socios || [];
  const socio = socios.find((item) => item.id === socioId);
  const camp = programa === "camp";
  const validProgram = camp || programa === "club";
  const registerClub = useRegisterClub();
  const registerCamp = useRegisterCamp();
  const [size, setSize] = React.useState("nino_m");
  const [error, setError] = React.useState("");
  const initialized = React.useRef(false);

  React.useEffect(() => {
    if (!socio || initialized.current) return;
    setSize(socio.camp?.talla || "nino_m");
    initialized.current = true;
  }, [socio]);

  if (isLoading) return <Box minH="100dvh" display="grid" placeItems="center"><Spinner color="brand.red"/></Box>;
  if (!validProgram || !socio) return <RedirectStatusPage to="/perfil/registro" eyebrow="Registro no encontrado" title="No encontramos este registro" description="Volveremos a tus registros para que puedas elegir una opción disponible." />;

  const program = camp ? socio.camp : socio.club;
  if (!program?.eligible && !program?.registered) return <RedirectStatusPage to="/perfil/registro" eyebrow="Registro no disponible" title="Este programa no está disponible" description="Este integrante no puede registrarse en el programa seleccionado. Volveremos a tus registros." />;

  const registered = Boolean(program.registered);
  const submitting = registerClub.isPending || registerCamp.isPending;
  const fullName = formatFullName(socio.nombre, socio.apellidoPaterno, socio.apellidoMaterno);
  const confirm = async () => {
    if (registered) { navigate("/perfil/registro", { replace: true }); return; }
    setError("");
    try {
      if (camp) await registerCamp.mutateAsync({ socioId: socio.id, talla: size });
      else await registerClub.mutateAsync({ socioId: socio.id });
      await refetch();
      navigate("/perfil/registro", { replace: true });
    } catch { setError("No pudimos completar el registro. Intenta nuevamente."); }
  };

  return <FamiliaEditorLayout
    title={camp ? "Registrar al Camp" : "Registrar al Club"}
    description={fullName}
    icon={camp ? <FiCompass/> : <FiShield/>}
    onSave={confirm}
    saveLabel={registered ? "Volver a registros" : "Confirmar registro"}
    busy={submitting}
    backTo="/perfil/registro"
    backLabel="Volver a Registros"
  >
    <Stack gap="7" maxW="560px">
      <Flex py="4" borderTopWidth="1px" borderBottomWidth="1px" borderColor="gray.100" align="center" justify="space-between">
        <Box><Text fontSize="xs" fontWeight="800" color="gray.400">PROGRAMA</Text><Text mt="1" fontWeight="800">{camp ? (socio.camp.campamentoNombre || "Camp Arawaks") : "Club Arawaks"}</Text></Box>
        <Flex px="3" py="2" rounded="full" bg={registered ? "green.50" : camp ? "blue.50" : "red.50"} color={registered ? "green.700" : camp ? "blue.700" : "red.700"} _dark={{ bg: registered ? "green.950" : camp ? "blue.950" : "red.950", color: registered ? "green.200" : camp ? "blue.200" : "red.200" }} align="center" gap="2"><FiCheck/><Text fontSize="xs" fontWeight="800">{registered ? "REGISTRADO" : "DISPONIBLE"}</Text></Flex>
      </Flex>

      {camp && !registered && <Box><Text mb="3" fontWeight="800">Talla de playera</Text><SimpleGrid columns="3" gap="2">{(socio.camp.allowedTallas || Object.keys(SIZES)).map((item) => {
        const selected = size === item;
        return <Button key={item} minH="48px" h="auto" py="2" rounded="14px" bg={selected ? "blue.50" : "public.bg.surface"} color={selected ? "blue.700" : "public.fg.default"} borderWidth="1px" borderColor={selected ? "blue.300" : "public.border.subtle"} _dark={{ bg: selected ? "blue.950" : "public.bg.surface", color: selected ? "blue.200" : "public.fg.default", borderColor: selected ? "blue.800" : "public.border.subtle" }} onClick={() => setSize(item)}>{SIZES[item] || item}</Button>;
      })}</SimpleGrid></Box>}

      {registered ? <Box p="4" rounded="18px" bg="green.50" color="green.800"><Text fontWeight="800">Registro confirmado</Text><Text mt="1" fontSize="sm">{"Este programa ya est\u00e1 activo para"} {socio.nombre}.</Text></Box> : <Text fontSize="sm" color="gray.500">{"Al confirmar, el registro quedar\u00e1 asociado a este hijo."}</Text>}
      {error && <Text color="red.600" fontSize="sm">{error}</Text>}
    </Stack>
  </FamiliaEditorLayout>;
}
